import { useState, useEffect } from 'react';
import { apiRequest } from 'lib/API';
import { endOfDay } from 'date-fns';
import { getUtcDate } from 'lib/utils';

/* Redux */
import { useDispatch, useSelector } from 'react-redux';
import { setActivities, setStatus, setLoaded } from 'app/slices/workSearchSlice';

/* API */
import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';

import { getWeekArr } from 'components/JobSearchActivity';

const getExperienceSkills = (item) => {
  if (!item || typeof item !== 'object') {
    return [];
  }

  const groups = [item.skills, item.parsed_skills, item.skills_gained];
  return groups.reduce((acc, group) => {
    if (Array.isArray(group) && group.length > 0) {
      return acc.concat(group);
    }
    return acc;
  }, []);
};

const getSkillKey = (skill) => {
  if (!skill) {
    return null;
  }

  if (typeof skill === 'string') {
    const normalized = skill.trim().toLowerCase();
    return normalized ? `name:${normalized}` : null;
  }

  if (typeof skill === 'object') {
    if (skill.id !== null && skill.id !== undefined) {
      return `id:${String(skill.id).trim().toLowerCase()}`;
    }

    const normalizedName = String(skill.name || skill.value || '')
      .trim()
      .toLowerCase();

    return normalizedName ? `name:${normalizedName}` : null;
  }

  return null;
};

export const collectUniqueSkills = (experienceData = {}) => {
  const jst = experienceData?.jst || {};
  const allExperienceItems = [
    ...(experienceData?.job_experience || []),
    ...(experienceData?.education || []),
    ...(jst?.military_courses || []),
    ...(jst?.military_experience || []),
    ...(jst?.other_learning || []),
    ...(jst?.academic_courses || []),
    ...(jst?.academic_degrees_certificates || []),
  ];

  const uniqueSkillsMap = new Map();

  allExperienceItems.forEach((item) => {
    const skills = getExperienceSkills(item);

    skills.forEach((skill) => {
      const key = getSkillKey(skill);
      if (key && !uniqueSkillsMap.has(key)) {
        uniqueSkillsMap.set(key, skill);
      }
    });
  });

  return Array.from(uniqueSkillsMap.values());
};

const useNotifications = () => {
  const dispatch = useDispatch();
  const myProfileNotificationShown = localStorage && localStorage.getItem('myProfileNotificationShown');
  const loaded_worksearch = useSelector((state) => state.worksearch.loaded);
  const worksearch_status = useSelector((state) => state.worksearch.status);
  const worksearch_activities = useSelector((state) => state.worksearch.activities);
  const { isAuthenticated } = useAuth0();

  const idQuery = useQuery({
    queryKey: ['/users/me'],
    enabled: isAuthenticated,
    retry: false,
    retryOnMount: false,
    refetchOnWindowFocus: false,
  });
  const additionalRequests = isAuthenticated && idQuery.isSuccess;

  const experienceQuery = useQuery({
    queryKey: ['/users/me/experience/'],
    refetchOnMount: 'always',
    cacheTime: 0,
    enabled: additionalRequests,
    refetchOnWindowFocus: false,
  });
  const likedSkills = collectUniqueSkills(experienceQuery?.data || {});

  const [weekArray, setWeekArray] = useState([]);
  const incompleteWeeks = weekArray.filter((week) => {
    // Create a variable for wee.to using startOfDay and one with the current date using startOfDay
    const weekTo = endOfDay(getUtcDate(new Date(week.to)));
    const currentDate = new Date();

    // If the week.to is greater than or equal to the current date, return true
    if (weekTo >= currentDate) {
      return true;
    }
  });

  useEffect(() => {
    if (idQuery.isSuccess && !loaded_worksearch) {
      apiRequest('GET', '/users/me/worksearch/').then(({ activities, status }) => {
        const weekArr = getWeekArr(status, activities);
        setWeekArray(weekArr);
        dispatch(setActivities(activities));
        dispatch(setStatus(status));
        if (!status.ssn_required) {
          dispatch(setLoaded(true));
        }
      });
    } else {
      const weekArr = getWeekArr(worksearch_status, worksearch_activities);
      setWeekArray(weekArr);
    }
  }, [worksearch_activities.length, idQuery.isSuccess]);

  return [incompleteWeeks, likedSkills, myProfileNotificationShown ? 0 : 1];
};

export default useNotifications;
